import { useCallback, useEffect, useRef, useState } from 'react';
import type { CapturePresetName } from '@/native';
import { randomId } from '@/lib/checksum';
import {
  addMedia,
  clearDraft,
  draftKey,
  listMedia,
  onDraftChange,
  readFields,
  removeMedia,
  writeFields,
  type DraftKind,
  type DraftMedia,
} from './store';

/** 表单里那一张照片 / 一段视频。`url` 是给 `<img>` / `<video>` 用的临时地址。 */
export interface DraftShot {
  id: string;
  blob: Blob;
  mimeType: string;
  url: string;
  capturedAt: Date;
  isVideo: boolean;
}

/**
 * 一张会自己活下来的表单（CR-037）。
 *
 * 用法和 `useState` 差不多，区别是每次改动都落盘：
 * 安卓在相机前台时把这个 Activity 回收掉，WebView 重新加载之后
 * 表单还是工人离开时的样子。
 *
 * **`userId` 为空时退化成普通的内存状态**（还没恢复会话、或者在浏览器里调试）。
 * 草稿必须按人分，宁可不存，也不能把 A 的草稿落成一份谁都读得到的。
 */
export function useFormDraft<F extends Record<string, unknown>>(
  kind: DraftKind,
  userId: number | null | undefined,
  initial: F,
  /** 被系统打断后要回到的地址。拍照前登记给恢复层用 */
  route: string,
) {
  const key = typeof userId === 'number' ? draftKey(kind, userId) : null;

  // 文字部分从 localStorage 同步读回来：第一帧就是恢复后的样子，不会先闪一张空表
  const [fields, setFieldsState] = useState<F>(() => ({
    ...initial,
    ...(key ? readFields<F>(key) : null),
  }));
  const [shots, setShots] = useState<DraftShot[]>([]);
  const [ready, setReady] = useState(key === null);
  /**
   * 第一次读库就已经有照片 —— 那是上次留下的，不是这次拍的。
   *
   * **不能用 `shots.length > 0` 代替**：工人在这一次里正常拍一张，那个判断也会变真，
   * 于是他刚拍完就被告知「这是你上次没提交完的内容」。
   */
  const [hadMedia, setHadMedia] = useState(false);
  const firstLoad = useRef(true);

  /**
   * 首帧读到的草稿里有没有东西 —— 用来告诉工人「这是上次没填完的」。
   *
   * **是 state 不是派生值**：它说的是「进来这一次」的事实。算成派生值的话，
   * 工人点了「清空」之后它还是 true（存下来的内容确实存在过），提示就撤不掉了。
   */
  const [hadFields, setHadFields] = useState(() => {
    if (!key) return false;
    const saved = readFields<F>(key);
    return Boolean(saved && Object.values(saved).some((v) => v !== '' && v != null));
  });

  // 卸载时回收 object URL。用 ref 拿最新的一份，effect 本身只跑一次
  const shotsRef = useRef<DraftShot[]>([]);
  shotsRef.current = shots;

  // `initial` 是调用处的字面量，每次渲染都是新对象。放进 ref 里，
  // 清空草稿时才拿得到它，又不会让 clear 每一帧换一个身份
  const initialRef = useRef(initial);
  initialRef.current = initial;

  useEffect(() => {
    return () => {
      for (const shot of shotsRef.current) URL.revokeObjectURL(shot.url);
    };
  }, []);

  /**
   * 从库里重新读一遍媒体。
   *
   * **已经有的 id 沿用旧的 object URL**：恢复层可能在表单挂载之后才把那张
   * 被打断的照片写进来（要重新解码、压缩，要花掉几百毫秒），
   * 每次都重建 URL 的话，屏幕上已经显示出来的图会闪一下。
   */
  const reload = useCallback(async () => {
    if (!key) return;
    let rows: DraftMedia[] = [];
    try {
      rows = await listMedia(key);
    } catch {
      // 读不出来就当没有草稿媒体。表单仍然可用
    }
    setShots((prev) => {
      const byId = new Map(prev.map((shot) => [shot.id, shot]));
      const next = rows.map(
        (row) =>
          byId.get(row.id) ?? {
            id: row.id,
            blob: row.blob,
            mimeType: row.mimeType,
            url: URL.createObjectURL(row.blob),
            capturedAt: new Date(row.capturedAt),
            isVideo: row.isVideo,
          },
      );
      const kept = new Set(next.map((shot) => shot.id));
      for (const shot of prev) if (!kept.has(shot.id)) URL.revokeObjectURL(shot.url);
      return next;
    });
    if (firstLoad.current) {
      firstLoad.current = false;
      if (rows.length > 0) setHadMedia(true);
    }
    setReady(true);
  }, [key]);

  useEffect(() => {
    void reload();
    // 恢复层在别处写进来的照片也要显示出来
    return onDraftChange((changed) => {
      if (changed === key) void reload();
    });
  }, [key, reload]);

  const setField = useCallback(
    <K extends keyof F>(name: K, value: F[K]) => {
      setFieldsState((prev) => {
        const next = { ...prev, [name]: value };
        if (key) writeFields(key, next);
        return next;
      });
    },
    [key],
  );

  const addShot = useCallback(
    async (input: { blob: Blob; mimeType: string; capturedAt: Date; isVideo?: boolean }) => {
      const id = randomId();
      const row: DraftMedia = {
        id,
        draftKey: key ?? '',
        blob: input.blob,
        mimeType: input.mimeType,
        capturedAt: input.capturedAt.getTime(),
        isVideo: input.isVideo ?? input.mimeType.startsWith('video/'),
        addedAt: Date.now(),
      };
      if (key) await addMedia(row);
      setShots((prev) => [
        ...prev,
        {
          id,
          blob: row.blob,
          mimeType: row.mimeType,
          url: URL.createObjectURL(row.blob),
          capturedAt: input.capturedAt,
          isVideo: row.isVideo,
        },
      ]);
    },
    [key],
  );

  const removeShot = useCallback(
    async (id: string) => {
      if (key) await removeMedia(id);
      setShots((prev) => {
        const target = prev.find((shot) => shot.id === id);
        if (target) URL.revokeObjectURL(target.url);
        return prev.filter((shot) => shot.id !== id);
      });
    },
    [key],
  );

  /** 提交成功、或者工人主动放弃时调用 */
  const clear = useCallback(async () => {
    if (key) await clearDraft(key);
    setShots((prev) => {
      for (const shot of prev) URL.revokeObjectURL(shot.url);
      return [];
    });
    setFieldsState(initialRef.current);
    setHadFields(false);
    setHadMedia(false);
  }, [key]);

  /** 拍照前交给 `capturePhoto` / `captureVideo` 的那条记录 */
  const captureSession = useCallback(
    (preset: CapturePresetName = 'normal') =>
      key ? { owner: key, route, preset } : undefined,
    [key, route],
  );

  return {
    /** 媒体读完了没有。没读完就渲染表单会让已拍的照片闪一下才出现 */
    ready,
    /** 这次进来是带着上次没提交完的内容 */
    restored: hadFields || hadMedia,
    fields,
    setField,
    shots,
    addShot,
    removeShot,
    clear,
    captureSession,
  };
}
